import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useParams } from 'react-router-dom';
import Image from 'components/AppImage';
import { DotLottieReact } from '@lottiefiles/dotlottie-react';
import successAnimation from 'assests/animations/success.json';
import failAnimation from 'assests/animations/failed.json';
import Lottie from 'lottie-react';
import PhoneInput from 'react-phone-input-2';
import 'react-phone-input-2/lib/style.css'


const Index = () => {
  const { slug } = useParams();
  const [isLoading, setIsLoading] = useState(true);
  const [scanData, setScanData] = useState(null);
  const [status, setStatus] = useState('loading');
  const [mobile, setMobile] = useState('');
  const [pin, setPin] = useState('');
  const [error, setError] = useState('');
  const [verificationLoading, setVerificationLoading] = useState(false);
  const [showFirstScanDate, setShowFirstScanDate] = useState(false)
  const [showIp, setShowIp] = useState(false)

  const scannedKey = `scanned_${slug}`;

  const formatIndianDateTime = (isoString) => {
    if (!isoString) return 'N/A';
    const date = new Date(isoString);
    return date.toLocaleString('en-IN', {
      day: 'numeric',
      month: 'long',
      year: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
      hour12: true,
      timeZone: 'Asia/Kolkata'
    });
  };

  const fetchScan = async (payload = {}) => {
    setIsLoading(true);
    try {
      const res = await axios.post(
        `${import.meta.env.VITE_APP_ADMINURL}/api/scan/${slug}`,
        payload,
        { headers: { 'x-app-source': 'Web' } }
      );
      setScanData(res.data);
      console.log(res.data);
      sessionStorage.setItem(scannedKey, JSON.stringify(res.data));
      setStatus('success');
    } catch (err) {
      const data = err.response?.data;
      const statusCode = err.response?.status;

      if (data?.status === 'verification_required') {
        setScanData(data);
        setStatus('verify');
      } else if (data?.status === 'app_only') {
        setStatus('app_only');
      } else if (data?.status === 'geo_required') {
        setScanData(data);
        setStatus('geo_verify');
        setError(data.message);
      } else if (data?.status === 'invalid_pin' || data?.status === 'mobile_required' || data?.status === 'geo_required') {
        // ✅ New handling
        setScanData(data);
        setStatus('verify');
        setError(data.message); // Display inline error under input
      } else if (statusCode === 404) {
        setStatus('not_found');
      } else {
        setStatus('error');
      }
    } finally {
      setIsLoading(false);
    }
  };
  const getGeoAndSubmit = () => {
    if (verificationLoading) return;
    if (!navigator.geolocation) {
      setError('Geolocation is not supported by your browser.');
      return;
    }
    setVerificationLoading(true);

    navigator.geolocation.getCurrentPosition(
      async (position) => {
        await fetchScan({
          mobile,
          pin,
          latitude: position.coords.latitude,
          longitude: position.coords.longitude
        });
        setVerificationLoading(false);
      },
      (error) => {
        if (error.code === error.PERMISSION_DENIED) {
          setError("Please allow location access.");
        } else if (error.code === error.POSITION_UNAVAILABLE) {
          setError("Location unavailable. Try enabling GPS.");
        } else if (error.code === error.TIMEOUT) {
          setError("Location request timed out.");
        }
        // setError('Location access denied. Please allow location sharing to continue.');
        setVerificationLoading(false);
      },
      {
        enableHighAccuracy: true,
        timeout: 10000,
        maximumAge: 0
      }
    );
  };
  useEffect(() => {
    if (!slug) {
      setStatus('no_qr_code');
      setIsLoading(false);
      return;
    }

    const alreadyScanned = sessionStorage.getItem(scannedKey);
    if (alreadyScanned) {
      setScanData(JSON.parse(alreadyScanned));
      setStatus('already_scanned');
      setIsLoading(false);
    } else {
      fetchScan();
    }
  }, [slug]);

  const handleVerificationSubmit = async () => {
    if (verificationLoading) return;
    if ((scanData.requireMobile && !mobile) || (scanData.requirePin && !pin)) {
      setError('Please enter the required details.');
      return;
    }
    setVerificationLoading(true);
    setError('');
    await fetchScan({ mobile, pin });
    setVerificationLoading(false);
  };

  const handlePhoneChange = (value, data, event, formattedValue) => {
    // Ensure "+" is always present
    const phoneWithPlus = value.startsWith('+') ? value : `+${value}`;
    setMobile(phoneWithPlus);
  };

  const renderMessage = () => {
    if (isLoading) return <p className="text-center">Checking QR code...</p>;

    if (status === 'no_qr_code') return <p className="text-center">No QR code detected</p>;
    if (status === 'not_found') return <p className="text-center text-red-500">QR Code Not Found</p>;
    if (status === 'error') return <p className="text-center text-red-500">Something went wrong</p>;
    if (status === 'app_only') return <p className="text-center text-yellow-600">This QR must be scanned via the official app.</p>;
    if (status === 'geo_verify') {
      return (
        <div className="space-y-4 text-center">
          <p className="font-medium text-gray-700">{scanData.message}</p>
          {scanData.requireMobile && (
            <input
              type="text"
              value={mobile}
              onChange={(e) => setMobile(e.target.value)}
              placeholder="Enter Mobile Number"
              className="w-full border p-2 rounded"
            />
          )}
          {scanData.requirePin && (
            <input
              type="password"
              value={pin}
              onChange={(e) => setPin(e.target.value)}
              placeholder="Enter PIN"
              className="w-full border p-2 rounded"
            />
          )}
          {error && <p className="text-red-600 text-sm">{error}</p>}
          <button onClick={getGeoAndSubmit} disabled={verificationLoading || isLoading} className="bg-blue-600 text-white px-4 py-2 rounded disabled:opacity-60 disabled:cursor-not-allowed">
            {verificationLoading || isLoading ? 'Submitting...' : 'Share Location & Submit'}
          </button>
        </div>
      );
    }
    if (status === 'verify') {

      return (
        <div className="space-y-4">
          <p className="text-center font-medium text-gray-700">{scanData.customNote || "Enter Data"}</p>
          {scanData.requireMobile && (
            <PhoneInput
              prefix='+'
              country={'in'}
              value={mobile}
              inputClass='p-2'
              inputStyle={{ width: "100%", outline: "none", boxShadow: 'none', borderColor: "#6b7280", fontSize: "1rem" }}
              // onChange={(phone) => setMobile(phone)}
              onChange={handlePhoneChange}
            />
          )}
          {scanData.requirePin && (
            <input
              type="password"
              value={pin}
              onChange={(e) => setPin(e.target.value)}
              placeholder="Enter PIN"
              className="w-full border p-2 rounded"
            />
          )}
          {error && <p className="text-red-600 text-sm">{error}</p>}
          <button onClick={handleVerificationSubmit} disabled={verificationLoading || isLoading} className="bg-blue-600 text-white px-4 py-2 rounded disabled:opacity-60 disabled:cursor-not-allowed">
            {verificationLoading || isLoading ? 'Submitting...' : 'Submit'}
          </button>
        </div>
      );
    }



    const isWarning = scanData?.message === 'Scan Limit Reached';
    const alertClasses = isWarning ? 'bg-red-100 text-red-700 border-red-400' : 'bg-green-100 text-green-700 border-green-400';
    const lottieSrc = isWarning ? "https://lottie.host/5680837d-b7a4-4129-a3e2-014c66f7d43f/ErYOw2CkdQ.lottie" : "https://lottie.host/f6913ea3-7a0e-42db-ba56-c206a647bc8e/6oTis3FUml.lottie"
    const animationData = isWarning ? failAnimation : successAnimation
    return (
      <div className={`p-4 border rounded shadow-md text-center ${alertClasses}`}>
        {/* <DotLottieReact
          src={lottieSrc}
          autoplay
        /> */}
        {scanData.product && (
          <div className="mt-4 mb-2 flex flex-col items-center gap-4">
            {scanData.productImage && (
              <Image src={`https://hiraadmin.opaldemo.com/${scanData.productImage}`} alt={scanData.product} className="h-[10dvh] justify-self-center object-contain" />
            )}
            <p className="text-sm md:text-base font-medium text-gray-800">Product: {scanData.product}</p>
          </div>
        )}
        <Lottie animationData={animationData} autoPlay loop={false} style={{ height: "10dvh", placeSelf: "center" }} />
        <h2 className="text-xl font-semibold mb-2">{scanData.message}</h2>
        <p className="text-sm md:text-xl">SL NO: <code>{slug}</code></p>
        {status === 'already_scanned' && (
          <p className="text-xs md:text-sm text-blue-600 font-medium mt-1">* Already scanned & page refreshed</p>
        )}
        {scanData.firstScanDate && scanData.show_first_scan && (
          <p className="mt-1 text-xs md:text-sm text-gray-600">First Scan At: {formatIndianDateTime(scanData.firstScanDate)}</p>
        )}
        <p className="text-xs md:text-sm text-gray-600">Scan Count: {scanData.scanCount}</p>
        {scanData.location && (
          <p className="text-xs md:text-sm text-gray-600">Location: {scanData.location}</p>
        )}
        {scanData.ip && scanData.show_ip &&
          <p className="text-xs md:text-sm text-gray-600">IP Address: {scanData.ip}</p>
        }

        {/* {scanData.custom_fields && scanData.custom_fields.map((cf, index) => {
          return (
            <a key={cf?.id || `${cf?.name || 'custom-field'}-${index}`} target='_blank' href={cf?.doc_url}>
              <div className='flex justify-center gap-1'>
              <img src={cf?.icon_url} width="20px" /><span className="text-xs md:text-sm text-gray-600">{cf.name}</span>
              </div>
            </a>
          )
        })} */}


        {!isWarning && scanData.custom_fields && (
          <div className="flex flex-col gap-3 mt-6">
            {scanData.custom_fields.map((cf, index) => (
              <a
                key={cf?.id || index}
                target='_blank'
                rel="noreferrer"
                href={cf?.doc_url}
                className="group bg-white border border-gray-100 rounded-xl p-3 shadow-sm hover:shadow-md hover:border-blue-200 transition-all active:scale-[0.98]"
              >
                <div className="grid grid-cols-[40px_1fr_30px] items-center gap-3 text-left">

                  {/* 1. Logo/Icon Container */}
                  <div className="w-10 h-10 flex items-center justify-center bg-gray-50 rounded-lg group-hover:bg-blue-50 transition-colors">
                    <img
                      src={cf?.icon_url}
                      className="w-6 h-6 object-contain"
                      alt=""
                      onError={(e) => { e.target.src = 'https://via.placeholder.com/24'; }} // Fallback
                    />
                  </div>

                  {/* 2. Text Content (Maximum Area) */}
                  <div className="flex flex-col overflow-hidden">
                    <span className="text-sm font-bold text-gray-800 truncate">
                      {cf.name}
                    </span>
                    <span className="text-[10px] text-gray-400 uppercase tracking-tight">
                      View Document
                    </span>
                  </div>

                  {/* 3. Action Icon */}
                  <div className="flex justify-end">
                    <div className="bg-gray-100 p-1 rounded-full group-hover:bg-green-500 transition-colors">
                      <svg
                        className="w-4 h-4 text-gray-400 group-hover:text-white"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M9 5l7 7-7 7" />
                      </svg>
                    </div>
                  </div>

                </div>
              </a>
            ))}
          </div>
        )
        }


        {
          (scanData.meta_url || scanData.instagram_url || scanData.linkedIn_url || scanData.youtube_url || scanData.x_url) &&
          <div className='mt-2 text-black '>
            <p>Connect with us : </p>
          </div>
        }
        <div className='flex mt-2 gap-2 items-center justify-center'>
          {/* <Icon name="Meta" /> */}
          {scanData.meta_url &&
            <div>
              <a href={scanData.meta_url}>
                <svg xmlns="http://www.w3.org/2000/svg" width="25" height="25" viewBox="0 0 25 25" fill="none">
                  <path d="M25 12.4998C25 5.59622 19.4035 -0.000229005 12.5 -0.000229005C5.59645 -0.000229005 0 5.59622 0 12.4998C0 18.362 4.03575 23.2809 9.4803 24.6317V16.3196H6.9027V12.4998H9.4803V10.8538C9.4803 6.59922 11.4057 4.62722 15.5827 4.62722C16.3746 4.62722 17.741 4.78247 18.3 4.93777V8.40047C18.005 8.36942 17.4926 8.35386 16.8559 8.35386C14.8063 8.35386 14.0143 9.13026 14.0143 11.1489V12.4998H18.0973L17.3959 16.3196H14.0143V24.9082C20.2036 24.1607 25 18.8907 25 12.4998Z" fill="#0866FF" />
                  <path d="M17.3955 16.3198L18.097 12.5H14.014V11.1491C14.014 9.13046 14.8059 8.35411 16.8556 8.35411C17.4922 8.35411 18.0046 8.36961 18.2997 8.40066V4.93802C17.7407 4.78272 16.3742 4.62742 15.5823 4.62742C11.4054 4.62742 9.47994 6.59946 9.47994 10.8541V12.5H6.90234V16.3198H9.47994V24.6319C10.4471 24.8718 11.4583 25 12.4996 25C13.0123 25 13.5175 24.9684 14.014 24.9084V16.3198H17.3955Z" fill="white" />
                </svg>
              </a>
            </div>
          }


          {/* <Icon name="Instagram" /> */}

          {scanData.instagram_url &&
            <div>
              <a href={scanData.instagram_url}>
                <svg xmlns="http://www.w3.org/2000/svg" width="25" height="25" viewBox="0 0 25 25" fill="none">
                  <g clipPath="url(#clip0_2945_42)">
                    <path d="M12.506 0.189453C7.36564 0.189453 5.86228 0.194756 5.57006 0.218999C4.51516 0.306688 3.85874 0.472786 3.14361 0.828847C2.59249 1.10252 2.15785 1.41976 1.72888 1.86445C0.947658 2.67544 0.474188 3.67317 0.302791 4.85915C0.219461 5.43491 0.195219 5.55233 0.190295 8.49324C0.188401 9.47354 0.190295 10.7637 0.190295 12.4942C0.190295 17.6306 0.195976 19.1324 0.220597 19.4241C0.305822 20.4506 0.466802 21.0965 0.8077 21.8029C1.4592 23.1552 2.70348 24.1703 4.16934 24.5491C4.6769 24.6798 5.23749 24.7518 5.95717 24.7859C6.26208 24.7991 9.36994 24.8086 12.4797 24.8086C15.5894 24.8086 18.6992 24.8048 18.9965 24.7896C19.8299 24.7504 20.3137 24.6855 20.8488 24.5472C22.3241 24.1665 23.5456 23.1665 24.2104 21.7953C24.5447 21.1059 24.7142 20.4355 24.7909 19.4626C24.8075 19.2504 24.8145 15.8684 24.8145 12.491C24.8145 9.11294 24.807 5.73718 24.7903 5.52506C24.7127 4.53642 24.5432 3.87165 24.1981 3.169C23.915 2.59381 23.6006 2.16426 23.1441 1.72506C22.3296 0.947029 21.3334 0.473544 20.1463 0.302332C19.5711 0.219188 19.4566 0.194567 16.5135 0.189453H12.506Z" fill="url(#paint0_radial_2945_42)" />
                    <path d="M12.506 0.189453C7.36564 0.189453 5.86228 0.194756 5.57006 0.218999C4.51516 0.306688 3.85874 0.472786 3.14361 0.828847C2.59249 1.10252 2.15785 1.41976 1.72888 1.86445C0.947658 2.67544 0.474188 3.67317 0.302791 4.85915C0.219461 5.43491 0.195219 5.55233 0.190295 8.49324C0.188401 9.47354 0.190295 10.7637 0.190295 12.4942C0.190295 17.6306 0.195976 19.1324 0.220597 19.4241C0.305822 20.4506 0.466802 21.0965 0.8077 21.8029C1.4592 23.1552 2.70348 24.1703 4.16934 24.5491C4.6769 24.6798 5.23749 24.7518 5.95717 24.7859C6.26208 24.7991 9.36994 24.8086 12.4797 24.8086C15.5894 24.8086 18.6992 24.8048 18.9965 24.7896C19.8299 24.7504 20.3137 24.6855 20.8488 24.5472C22.3241 24.1665 23.5456 23.1665 24.2104 21.7953C24.5447 21.1059 24.7142 20.4355 24.7909 19.4626C24.8075 19.2504 24.8145 15.8684 24.8145 12.491C24.8145 9.11294 24.807 5.73718 24.7903 5.52506C24.7127 4.53642 24.5432 3.87165 24.1981 3.169C23.915 2.59381 23.6006 2.16426 23.1441 1.72506C22.3296 0.947029 21.3334 0.473544 20.1463 0.302332C19.5711 0.219188 19.4566 0.194567 16.5135 0.189453H12.506Z" fill="url(#paint1_radial_2945_42)" />
                    <path d="M12.5006 3.40918C10.0317 3.40918 9.72187 3.41998 8.7522 3.4641C7.78443 3.50842 7.12384 3.66164 6.54583 3.88645C5.94793 4.11865 5.44075 4.42926 4.93546 4.93475C4.42979 5.44005 4.1192 5.94725 3.88625 6.54497C3.66088 7.12319 3.50747 7.78399 3.46391 8.75141C3.42054 9.72111 3.40918 10.0311 3.40918 12.5001C3.40918 14.969 3.42016 15.2779 3.4641 16.2476C3.50861 17.2154 3.66182 17.876 3.88644 18.4541C4.11882 19.052 4.42941 19.5592 4.93489 20.0645C5.43999 20.5702 5.94717 20.8815 6.54469 21.1137C7.12308 21.3385 7.78386 21.4918 8.75144 21.5361C9.72111 21.5802 10.0308 21.591 12.4994 21.591C14.9685 21.591 15.2774 21.5802 16.247 21.5361C17.2148 21.4918 17.8762 21.3385 18.4546 21.1137C19.0523 20.8815 19.5587 20.5702 20.0638 20.0645C20.5695 19.5592 20.8801 19.052 21.113 18.4543C21.3365 17.876 21.4899 17.2152 21.5353 16.2478C21.5789 15.2781 21.5903 14.969 21.5903 12.5001C21.5903 10.0311 21.5789 9.7213 21.5353 8.7516C21.4899 7.7838 21.3365 7.12319 21.113 6.54516C20.8801 5.94725 20.5695 5.44005 20.0638 4.93475C19.5581 4.42907 19.0525 4.11846 18.454 3.88645C17.8745 3.66164 17.2135 3.50842 16.2457 3.4641C15.2761 3.41998 14.9674 3.40918 12.4977 3.40918H12.5006ZM11.6851 5.04744C11.9271 5.04706 12.1972 5.04744 12.5006 5.04744C14.9278 5.04744 15.2155 5.05615 16.1739 5.09971C17.0603 5.14024 17.5413 5.28835 17.8618 5.41278C18.286 5.57755 18.5885 5.77452 18.9064 6.0927C19.2246 6.41088 19.4216 6.71391 19.5867 7.13816C19.7111 7.45823 19.8594 7.93929 19.8998 8.82566C19.9433 9.78399 19.9528 10.0719 19.9528 12.498C19.9528 14.9241 19.9433 15.212 19.8998 16.1704C19.8592 17.0567 19.7111 17.5378 19.5867 17.8579C19.422 18.2821 19.2246 18.5842 18.9064 18.9022C18.5883 19.2204 18.2862 19.4173 17.8618 19.5821C17.5417 19.7071 17.0603 19.8548 16.1739 19.8954C15.2156 19.9389 14.9278 19.9484 12.5006 19.9484C10.0732 19.9484 9.7855 19.9389 8.8272 19.8954C7.94086 19.8544 7.45981 19.7063 7.13918 19.5819C6.71495 19.4171 6.41193 19.2202 6.09376 18.902C5.77559 18.5838 5.57862 18.2815 5.41348 17.8571C5.28905 17.537 5.14076 17.056 5.10042 16.1696C5.05686 15.2113 5.04815 14.9234 5.04815 12.4957C5.04815 10.0681 5.05686 9.78172 5.10042 8.82338C5.14095 7.93702 5.28905 7.45596 5.41348 7.13551C5.57824 6.71126 5.77559 6.40823 6.09376 6.09005C6.41193 5.77187 6.71495 5.5749 7.13918 5.40975C7.45963 5.28475 7.94086 5.13702 8.8272 5.0963C9.66581 5.05842 9.9908 5.04706 11.6851 5.04516V5.04744ZM17.3531 6.55691C16.7508 6.55691 16.2622 7.04497 16.2622 7.64744C16.2622 8.24971 16.7508 8.73835 17.3531 8.73835C17.9553 8.73835 18.444 8.24971 18.444 7.64744C18.444 7.04516 17.9553 6.55653 17.3531 6.55653V6.55691ZM12.5006 7.83153C9.92243 7.83153 7.83215 9.92187 7.83215 12.5001C7.83215 15.0783 9.92243 17.1677 12.5006 17.1677C15.0787 17.1677 17.1682 15.0783 17.1682 12.5001C17.1682 9.92187 15.0787 7.83153 12.5006 7.83153ZM12.5006 9.46979C14.174 9.46979 15.5308 10.8264 15.5308 12.5001C15.5308 14.1736 14.174 15.5304 12.5006 15.5304C10.8269 15.5304 9.47036 14.1736 9.47036 12.5001C9.47036 10.8264 10.8269 9.46979 12.5006 9.46979Z" fill="white" />
                  </g>
                  <defs>
                    <radialGradient id="paint0_radial_2945_42" cx="0" cy="0" r="1" gradientUnits="userSpaceOnUse" gradientTransform="translate(6.73053 26.7048) rotate(-90) scale(24.3994 22.6988)">
                      <stop stopColor="#FFDD55" />
                      <stop offset="0.1" stopColor="#FFDD55" />
                      <stop offset="0.5" stopColor="#FF543E" />
                      <stop offset="1" stopColor="#C837AB" />
                    </radialGradient>
                    <radialGradient id="paint1_radial_2945_42" cx="0" cy="0" r="1" gradientTransform="matrix(2.14124 10.6945 -44.0928 8.82892 -3.93538 1.96297)" gradientUnits="userSpaceOnUse">
                      <stop stopColor="#3771C8" />
                      <stop offset="0.128" stopColor="#3771C8" />
                      <stop offset="1" stopColor="#6600FF" stopOpacity="0" />
                    </radialGradient>
                    <clipPath id="clip0_2945_42">
                      <rect width="25" height="25" fill="white" />
                    </clipPath>
                  </defs>
                </svg>
              </a>
            </div>
          }

          {/* <Icon name="Linkedin" /> */}
          {scanData.linkedIn_url &&
            <div>
              <a href={scanData.linkedIn_url}>
                <svg xmlns="http://www.w3.org/2000/svg" width="25" height="25" viewBox="0 0 25 25" fill="none">
                  <path d="M22.7385 0H2.26145C1.0125 0 0 1.0125 0 2.26145V22.7385C0 23.9875 1.0125 25 2.26145 25H22.7385C23.9875 25 25 23.9875 25 22.7385V2.26145C25 1.0125 23.9875 0 22.7385 0ZM7.73606 21.5866C7.73606 21.9501 7.44143 22.2448 7.07795 22.2448H4.27651C3.91302 22.2448 3.61839 21.9501 3.61839 21.5866V9.84313C3.61839 9.47965 3.91302 9.18501 4.27651 9.18501H7.07795C7.44143 9.18501 7.73606 9.47965 7.73606 9.84313V21.5866ZM5.67723 8.07801C4.2074 8.07801 3.01584 6.88645 3.01584 5.41662C3.01584 3.94679 4.2074 2.75524 5.67723 2.75524C7.14706 2.75524 8.33861 3.94679 8.33861 5.41662C8.33861 6.88645 7.14712 8.07801 5.67723 8.07801ZM22.3763 21.6397C22.3763 21.9738 22.1054 22.2448 21.7712 22.2448H18.7651C18.4309 22.2448 18.1599 21.9738 18.1599 21.6397V16.1313C18.1599 15.3096 18.401 12.5304 16.0125 12.5304C14.1598 12.5304 13.784 14.4327 13.7086 15.2863V21.6397C13.7086 21.9738 13.4377 22.2448 13.1035 22.2448H10.196C9.86185 22.2448 9.5909 21.9738 9.5909 21.6397V9.79012C9.5909 9.45596 9.86185 9.18501 10.196 9.18501H13.1035C13.4376 9.18501 13.7086 9.45596 13.7086 9.79012V10.8147C14.3955 9.7837 15.4165 8.98796 17.5902 8.98796C22.4039 8.98796 22.3763 13.4851 22.3763 15.956V21.6397Z" fill="#0077B7" />
                </svg>
              </a>
            </div>
          }


          {/* twitter */}
          {scanData.x_url &&
            <div>
              <a href={scanData.x_url}>
                <svg xmlns="http://www.w3.org/2000/svg" width="25" height="25" viewBox="0 0 25 25" fill="none">
                  <rect width="25" height="25" rx="2" fill="black" />
                  <path d="M18.75 4H21.8175L15.1175 11.6246L23 22H16.8287L11.995 15.7062L6.46375 22H3.395L10.5612 13.8441L3 4H9.32875L13.6975 9.75131L18.75 4ZM17.675 20.1727H19.375L8.40375 5.73172H6.58125L17.675 20.1727Z" fill="white" />
                </svg>
              </a>
            </div>
          }

          {/* Youtube */}
          {scanData.youtube_url &&
            <div>
              <a href={scanData.youtube_url}>
                <svg xmlns="http://www.w3.org/2000/svg" width="25" height="19" viewBox="0 0 25 19" fill="none">
                  <path d="M19.8078 0.654297H5.19218C2.32462 0.654297 0 2.97891 0 5.84648V13.1527C0 16.0203 2.32462 18.3449 5.19218 18.3449H19.8078C22.6754 18.3449 25 16.0203 25 13.1527V5.84648C25 2.97891 22.6754 0.654297 19.8078 0.654297ZM16.2964 9.85509L9.46017 13.1155C9.27802 13.2024 9.06761 13.0696 9.06761 12.8678V6.14311C9.06761 5.93845 9.28355 5.8058 9.46609 5.89832L16.3023 9.36257C16.5055 9.46556 16.502 9.75704 16.2964 9.85509Z" fill="#F61C0D" />
                </svg>
              </a>
            </div>
          }




        </div>
      </div >
    );
  };


  useEffect(() => {
    const isWarning = scanData?.message === 'Scan Limit Reached';
    if (status === 'success' && scanData?.redirect_url && !isWarning) {
      const timer = setTimeout(() => {
        window.location.href = scanData.redirect_url;
      }, 7000);
      return () => clearTimeout(timer)
    }
  }, [status, scanData]);

  return (
    <div className="min-h-[100dvh] flex items-center justify-center p-4 font-sans">
      <div className="bg-white p-4 rounded-xl shadow-2xl w-full max-w-md">
        <h1 className="text=lg md:text-xl font-bold  text-center text-gray-800 mb-4">QR Authentication</h1>
        {renderMessage()}
      </div>
    </div>
  );
};

export default Index;
